import React, { useState, useCallback } from "react";
import TopNavigation from "components/ui/TopNavigation";
import Icon from "components/AppIcon";
import KPICard from "./components/KPICard";
import RevenueWaterfallChart from "./components/RevenueWaterfallChart";
import AgingReportSidebar from "./components/AgingReportSidebar";
import PaymentTrackingTable from "./components/PaymentTrackingTable";
import GlobalControls from "./components/GlobalControls";
import MonthlyTrendChart from "./components/MonthlyTrendChart";

const kpiData = [
  {
    title: "Total Revenue (MTD)",
    value: "$67,500",
    subtitle: "Mar 1–4, 2026",
    icon: "DollarSign",
    trendValue: 8.2,
    colorClass: "#0F766E",
    bgClass: "bg-teal-50",
    threshold: 82,
  },
  {
    title: "Pending Payments",
    value: "$12,300",
    subtitle: "43 invoices outstanding",
    icon: "Clock",
    trendValue: -5.1,
    colorClass: "#D97706",
    bgClass: "bg-amber-50",
    threshold: 38,
  },
  {
    title: "Collection Rate",
    value: "84.6%",
    subtitle: "Target: 90%",
    icon: "TrendingUp",
    trendValue: 2.3,
    colorClass: "#059669",
    bgClass: "bg-emerald-50",
    threshold: 85,
  },
  {
    title: "Partial Payments",
    value: "$2,760",
    subtitle: "8 members · avg 52% paid",
    icon: "CircleDot",
    trendValue: -1.8,
    colorClass: "#2563EB",
    bgClass: "bg-blue-50",
    threshold: 52,
  },
  {
    title: "Invoice Success Rate",
    value: "96.2%",
    subtitle: "312 of 324 generated",
    icon: "FileText",
    trendValue: 1.1,
    colorClass: "#7C3AED",
    bgClass: "bg-violet-50",
    threshold: 96,
  },
  {
    title: "Reminder Effectiveness",
    value: "31.4%",
    subtitle: "WhatsApp + Email + SMS",
    icon: "Send",
    trendValue: 4.7,
    colorClass: "#EA580C",
    bgClass: "bg-orange-50",
    threshold: 31,
  },
];

export default function RevenueOperations() {
  const [branch, setBranch] = useState("All Branches");
  const [paymentMethod, setPaymentMethod] = useState("All Methods");
  const [agingPeriod, setAgingPeriod] = useState("All Periods");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("Just now");

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
      setLastUpdated("Just now");
    }, 1800);
  }, []);

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <TopNavigation />
      <main className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 py-4 lg:py-6 space-y-4 lg:space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-[var(--color-primary)] flex items-center justify-center">
                <Icon name="TrendingUp" size={16} color="#FFFFFF" strokeWidth={2.5} />
              </div>
              <h1 className="text-xl lg:text-2xl font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">Revenue Operations</h1>
            </div>
            <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] ml-10">
              Financial monitoring · Payment tracking · Collection analytics
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg px-3 py-2">
            <Icon name="Calendar" size={13} color="var(--color-muted-foreground)" />
            <span>March 2026 · MTD</span>
          </div>
        </div>

        {/* Global Controls */}
        <GlobalControls
          branch={branch}
          setBranch={setBranch}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          agingPeriod={agingPeriod}
          setAgingPeriod={setAgingPeriod}
          isRefreshing={isRefreshing}
          onRefresh={handleRefresh}
          lastUpdated={lastUpdated}
        />

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
          {kpiData?.map((kpi) => (
            <KPICard key={kpi?.title} {...kpi} />
          ))}
        </div>

        {/* Main Content: Chart + Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Waterfall Chart - 2/3 width */}
          <div className="lg:col-span-2">
            <RevenueWaterfallChart />
          </div>
          {/* Aging Sidebar - 1/3 width */}
          <div className="lg:col-span-1">
            <AgingReportSidebar />
          </div>
        </div>

        {/* Monthly Trend */}
        <MonthlyTrendChart />

        {/* Payment Tracking Table */}
        <PaymentTrackingTable />

        {/* Footer */}
        <footer className="text-center py-4 border-t border-[var(--color-border)]">
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
            &copy; {new Date()?.getFullYear()} myfitnesstracking Analytics · Revenue Operations Dashboard · Data refreshes every 5 minutes
          </p>
        </footer>
      </main>
    </div>
  );
}